import React, { useMemo } from 'react';

import { Box, Flex, FlexProps, SimpleGrid, Space, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { EditAvatar } from 'components/EditAvatar';
import { DiscordIntegrationSection } from 'components/SmartAssistantSections/MessagingSmartAssistant/DiscordIntegrationSection';
import { FacebookIntegrationSection } from 'components/SmartAssistantSections/MessagingSmartAssistant/FacebookIntegrationSection';
import { InstagramIntegrationSection } from 'components/SmartAssistantSections/MessagingSmartAssistant/InstagramIntegrationSection';
import { WhatsAppIntegrationSection } from 'components/SmartAssistantSections/MessagingSmartAssistant/WhatsAppIntegrationSection';
import { useSmartAssistantAvatarUrlHandling } from 'hooks/use-smart-assistant-avatar-url-handling';
import { useTranslation } from 'react-i18next';
import { MAX_WIDTH } from 'shared/constants/theme';
import {
  DiscordIntegartionFormValues,
  FacebookIntegrationFormValues,
  InstagramIntegrationFormValues,
  MessagingSmartAssistantBasicsFormValues,
  SmartAssistant,
  WhatsAppIntegrationFormValues
} from 'shared/interfaces/SmartAssistant';

interface IntegrationForm {
  discordForm?: UseFormReturnType<DiscordIntegartionFormValues>;
  facebookForm?: UseFormReturnType<FacebookIntegrationFormValues>;
  instagramForm?: UseFormReturnType<InstagramIntegrationFormValues>;
  whatsAppForm?: UseFormReturnType<WhatsAppIntegrationFormValues>;
}

interface MessagingSmartAssistantBasicsSectionProps extends FlexProps {
  basicsForm: UseFormReturnType<MessagingSmartAssistantBasicsFormValues>;
  integrationForm: IntegrationForm;
  smartAssistant: SmartAssistant;
}

export function MessagingBasicsSection({
  basicsForm,
  integrationForm,
  smartAssistant,
  ...flexProps
}: MessagingSmartAssistantBasicsSectionProps) {
  const { t } = useTranslation();
  const { backToDefaultAvatar, handleImageDrop, isUpdatingAvatar } = useSmartAssistantAvatarUrlHandling(smartAssistant);

  const IntegrationSection = useMemo(() => {
    if (integrationForm.discordForm) return <DiscordIntegrationSection form={integrationForm.discordForm} />;

    if (integrationForm.whatsAppForm)
      return <WhatsAppIntegrationSection form={integrationForm.whatsAppForm} smartAssistant={smartAssistant} />;

    if (integrationForm.facebookForm)
      return <FacebookIntegrationSection form={integrationForm.facebookForm} smartAssistant={smartAssistant} />;

    if (integrationForm.instagramForm)
      return <InstagramIntegrationSection form={integrationForm.instagramForm} smartAssistant={smartAssistant} />;

    return null;
  }, [integrationForm, smartAssistant]);

  return (
    <Flex w="100%" direction="row" align="flex-start" justify="space-between" maw={MAX_WIDTH} {...flexProps}>
      <SimpleGrid cols={2} breakpoints={[{ maxWidth: 'md', cols: 1 }]} w="100%" px="md">
        <Flex w="100%" direction="row" align="flex-start">
          <EditAvatar
            src={smartAssistant.avatarUrl}
            size={50}
            radius={25}
            onRemove={backToDefaultAvatar}
            onFileDrop={handleImageDrop}
            loading={isUpdatingAvatar}
            mt={6}
          />
          <Space w="sm" />
          <TextInput
            w="100%"
            label={t('SMART_ASSISTANT.NAME.LABEL')}
            placeholder={t('SMART_ASSISTANT.NAME.PLACEHOLDER')}
            {...basicsForm.getInputProps('name')}
          />
        </Flex>
        <Box w="100%" maw={400}>
          {IntegrationSection}
        </Box>
      </SimpleGrid>
    </Flex>
  );
}
